
import { defineComponent } from "vue";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {transactionhistory, getFifthPairOptionDetails, saveFifthPairOption}  from "@/api/transaction";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

dayjs.extend(customParseFormat);

type SortKey = "transactionID" | "txndte" | "rusid" | "transactiondesc" | "txnamount";
type SortDirection = "asc" | "desc";

const TRANSACTION_DATE_FORMATS = [
  "MMM D YYYY h:mmA",
  "MMM DD YYYY h:mmA",
  "MMM D YYYY h:mm A",
  "MMM DD YYYY h:mm A",
  "MMM D YYYY hh:mmA",
  "MMM DD YYYY hh:mmA",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "MM/DD/YYYY HH:mm:ss",
  "MM/DD/YYYY h:mm:ss A",
  "MM/DD/YYYY hh:mm:ss A",
  "MM/DD/YYYY",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm",
  "DD/MM/YYYY",
  "MMM D, YYYY hh:mm A",
  "MMM DD, YYYY hh:mm A",
  "MMM D YYYY hh:mm A",
  "dddd MMMM D, YYYY",
];

export default defineComponent({
  name: "kt-widget-11",
  computed: {
    sortedList(): Record<string, unknown>[] {
      const items = [...(this.list || [])];
      const direction = this.sortDirection === "asc" ? 1 : -1;

      return items.sort((a, b) => {
        const result = this.compareValues(
          this.getItemField(a, this.sortKey),
          this.getItemField(b, this.sortKey),
          this.sortKey
        );
        return result * direction;
      });
    },
  },
  methods: {
    getRowKey(item: Record<string, unknown>, index: number): unknown {
      return this.getItemField(item, "transactionID") ?? index;
    },
    getItemField(item: Record<string, unknown>, key: string): unknown {
      if (item[key] !== undefined && item[key] !== null) {
        return item[key];
      }

      const lowerKey = key.toLowerCase();
      const matchedKey = Object.keys(item).find(
        (itemKey) => itemKey.toLowerCase() === lowerKey
      );

      return matchedKey ? item[matchedKey] : undefined;
    },
    normalizeTransactionDate(value: unknown): string {
      const raw = String(value ?? "").trim().replace(/\s+/g, " ");
      if (!raw) {
        return "";
      }

      return raw.replace(
        /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
        (_match, month, day, year, hour, minute, meridiem) => {
          const normalizedMonth =
            month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
          return `${normalizedMonth} ${day} ${year} ${hour}:${minute}${meridiem.toUpperCase()}`;
        }
      );
    },
    parseTransactionDate(value: unknown): number | null {
      const raw = this.normalizeTransactionDate(value);
      if (!raw) {
        return null;
      }

      let parsed = dayjs(raw, TRANSACTION_DATE_FORMATS, true);
      if (parsed.isValid()) {
        return parsed.valueOf();
      }

      parsed = dayjs(raw);
      if (parsed.isValid()) {
        return parsed.valueOf();
      }

      const slashMatch = raw.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?)?$/i
      );
      if (slashMatch) {
        const [, part1, part2, year, hour = "0", minute = "0", second = "0", meridiem] = slashMatch;
        const p1 = Number(part1);
        const p2 = Number(part2);
        const dateCandidates: Array<{ day: number; month: number }> = [];

        if (p1 > 12) {
          dateCandidates.push({ day: p1, month: p2 });
        } else if (p2 > 12) {
          dateCandidates.push({ day: p2, month: p1 });
        } else {
          dateCandidates.push({ day: p2, month: p1 });
          dateCandidates.push({ day: p1, month: p2 });
        }

        let hour24 = Number(hour);
        if (meridiem) {
          if (meridiem.toUpperCase() === "PM" && hour24 < 12) {
            hour24 += 12;
          }
          if (meridiem.toUpperCase() === "AM" && hour24 === 12) {
            hour24 = 0;
          }
        }

        for (const candidate of dateCandidates) {
          parsed = dayjs(
            `${year}-${candidate.month.toString().padStart(2, "0")}-${candidate.day
              .toString()
              .padStart(2, "0")} ${hour24.toString().padStart(2, "0")}:${minute}:${second}`
          );
          if (parsed.isValid()) {
            return parsed.valueOf();
          }
        }
      }

      return null;
    },
    toggleSort(key: SortKey) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
        return;
      }

      this.sortKey = key;
      this.sortDirection = key === "txndte" ? "desc" : "asc";
    },
    sortIconClass(key: SortKey) {
      if (this.sortKey !== key) {
        return "bi bi-arrow-down-up text-muted fs-7";
      }

      return this.sortDirection === "asc"
        ? "bi bi-sort-up text-dark fs-7"
        : "bi bi-sort-down text-dark fs-7";
    },
    compareValues(a: unknown, b: unknown, key: SortKey) {
      if (key === "transactionID" || key === "txnamount") {
        const numA = Number(a) || 0;
        const numB = Number(b) || 0;
        return numA - numB;
      }

      if (key === "txndte") {
        const timeA = this.parseTransactionDate(a);
        const timeB = this.parseTransactionDate(b);

        if (timeA === null && timeB === null) {
          return 0;
        }
        if (timeA === null) {
          return 1;
        }
        if (timeB === null) {
          return -1;
        }

        return timeA - timeB;
      }

      return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    },
    async transactionhistory(){ 
      this.loading = true;
      try {
        const response = await transactionhistory(1);
        this.list = response.data?.pageList ?? [];
      } catch (error) {
        this.list = [];
      } finally {
        this.loading = false;
      }
    },
    async getFifthPairOptionDetailsFN(){
      const response = await getFifthPairOptionDetails();
      this.pr_5th = response.data.fifthPairLastOption;
      this.isAllowedFifthPairOption = response.data.isAllowedFifthPairOption;
    },

    async saveFifthPairOptionFN(){
      var params = {"pr_5th" : this.pr_5th}
      const response = await saveFifthPairOption(params);
       if(response != null){
          this.loading = false;
          if (response.data.result == "SUCCESS") {
            Swal.fire({
              title: "",
              text: response.data.message,
              icon: "success",
              confirmButtonClass: "btn btn-secondary"
            }); 
           // window.location.reload();
            return; 
          } else if(response.data.result == "FAILED"){
            Swal.fire({
              title: "",
              text: response.data.message,
              icon: "error",
              confirmButtonClass: "btn btn-secondary"
            }); 
            return; 
          }
        }
      
    }

  },
  mounted(){
      this.transactionhistory();
      this.getFifthPairOptionDetailsFN();
  },
  data(){
      return { 
        list : [] as Record<string, unknown>[],
        loading: true,
        pr_5th : "GC",
        isAllowedFifthPairOption : false,
        sortKey: "txndte" as SortKey,
        sortDirection: "desc" as SortDirection,
      }
  },
  components: {},
  props: {
    widgetClasses: String
  }
});
